import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportNameAction } from '@/api/models';
import {
    useAllBrandsQuery,
    useCatalogImportPreviewMutation,
    useCategoryPickerQuery,
    useSupplierPickerQuery,
} from '@/api/queries';
import {
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';
import { buildCatalogImportRequest } from '@/pages/catalogImport/parse';
import {
    nameTextKey,
    useCatalogImport,
    type NameSuggestions,
} from '@/pages/catalogImport/store';
import { useResetOnKeyChange } from '@/utils/useResetOnKeyChange';
import {
    candidateOptionsOf,
    categoryOptionsOf,
    namedOptionsOf,
    type NameOption,
} from './nameOptions';
import {
    NAMES_CHECK_IDLE,
    namesCheckOfError,
    namesCheckOfPreview,
    type NamesCheck,
} from './namesCheck';
import {
    acceptAll,
    incompleteCount,
    isGainingOptions,
    isNameSettled,
    isSameDraft,
    joinedPartsOf,
    mergeDrafts,
    NAME_ACTIONS,
    NAME_KINDS,
    namesDecisionsOf,
    reviewOf,
    shownAxesOf,
    showsAxes,
    targetKeyOf,
    typoLabelOf,
    targetOfKey,
    unsortedRowCount,
    type NameDraft,
    type NameDrafts,
    type NameKind,
    type NameMatch,
    type NamesReview,
} from './namesDecisions';

export type UseNamesStepOptions = {
    /** Where Back goes: Upload. */
    backPath: string;
    /** Where a clean check leads: Products. */
    nextPath: string;
};

/** A key as a picker or the segmented control emits it. */
export type PickerKey = string | number | null;

export type NameRowModel = {
    /** The text as the file has it: the name's identity. */
    text: string;
    /** Rows of the file that use it. */
    rows: number;
    /**
     * `new` offers the actions; `existing` (a category listed for its axes)
     * and `ambiguous` (several entities share the name) do not.
     */
    match: NameMatch;
    draft: NameDraft;
    /** Use existing's choice as a picker key. */
    targetKey: string | null;
    /** The other new texts of the same kind, Use existing's second group. */
    fileOptions: NameOption[];
    /** The likely correction the preview suggests ("looks like a typo · …"), if any. */
    suggestion: string | null;
    /**
     * A joined text ("Alika,Kidobani") suggested its first name: how many
     * names the cell lists, for the sentence that says why; `null` otherwise.
     * Kept after a re-check (the store remembers the suggestion).
     */
    joinedParts: number | null;
    /** An ambiguous name: the entities that share it, one to pick. */
    candidates: NameOption[];
    /** An existing name: its entity as the pickers label it. */
    existingLabel: string | null;
    /**
     * The axes the import adds to the category, by name, while the row shows
     * them with the checkbox (`draft.addAxes`); empty otherwise.
     */
    axesToAdd: string[];
    /** Ready for Check products: the action's value, a candidate picked. */
    isComplete: boolean;
    /**
     * Why the preview could not resolve this name as decided, while the draft
     * is still the one that preview answered; an edit clears it until the
     * next check.
     */
    notices: CatalogImportNotice[];
};

export type NameSectionModel = {
    kind: NameKind;
    actions: readonly CatalogImportNameAction[];
    /** The new names (and the ambiguous ones): what the section's title counts. */
    rows: NameRowModel[];
    /**
     * Categories the catalogue already has that the import adds axes to
     * (`isGainingOptions`), listed apart from the new ones; empty for the
     * other kinds.
     */
    gainingOptions: NameRowModel[];
    /** File rows whose cell lists several names and was suggested the first (`joinedParts`). */
    joinedRows: number;
};

/** The catalogue's brands, categories and suppliers, for Use existing and Nest under. */
export type NamesCatalog =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; options: Record<NameKind, NameOption[]> };

export type UseNamesStepResult = {
    /** The store holds a preview to review; without one the step cannot run. */
    hasPreview: boolean;
    /** Brands, categories and suppliers, always in that order. */
    sections: NameSectionModel[];
    /** Rows with an empty Category cell, which stay Unsorted. */
    unsortedRows: number;
    catalog: NamesCatalog;
    onRetryCatalog: () => void;
    check: NamesCheck;
    /** Names whose action still lacks its value (a pick, a name, a parent). */
    incomplete: number;
    /** Every name has what its action needs; a check already running is `check.status`. */
    canContinue: boolean;
    onActionChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onTargetChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onNameChange: (kind: NameKind, text: string, name: string) => void;
    onParentChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onAddAxesChange: (kind: NameKind, text: string, addAxes: boolean) => void;
    onAcceptAll: (kind: NameKind) => void;
    onBack: () => void;
    /** Check products: stores the decisions and checks the file again with them. */
    onContinue: () => void;
};

const EMPTY_NAMES = { brands: [], categories: [], suppliers: [] };

const isAction = (
    kind: NameKind,
    key: PickerKey,
): key is CatalogImportNameAction =>
    typeof key === 'string' &&
    (NAME_ACTIONS[kind] as readonly string[]).includes(key);

/** Each reviewed name's Use existing targets among the file's other new texts. */
type FileOptions = Record<NameKind, ReadonlyMap<string, NameOption[]>>;

/**
 * The file's new texts as picker options, once per review: the same option
 * objects across edits, so the picker's list is not rebuilt as the owner
 * types (`NamePicker`). Only `new` texts: an existing or ambiguous one is
 * not new in this file.
 */
const fileOptionsOf = (review: NamesReview): FileOptions => {
    const byKind = {} as Record<NameKind, Map<string, NameOption[]>>;
    for (const kind of NAME_KINDS) {
        const texts = review.entries[kind]
            .filter((entry) => entry.match === 'new')
            .map((entry) => entry.text);
        const options = texts.map((text) => ({
            id: targetKeyOf({ text }) ?? text,
            label: text,
            isFileText: true,
        }));
        byKind[kind] = new Map(
            review.entries[kind].map((entry) => [
                entry.text,
                options.filter((option) => option.label !== entry.text),
            ]),
        );
    }
    return byKind;
};

const rowsOf = (
    kind: NameKind,
    review: NamesReview,
    drafts: NameDrafts,
    fileOptions: FileOptions,
    existing: readonly NameOption[],
    suggestions: NameSuggestions,
): NameRowModel[] => {
    const entries = review.entries[kind];
    return entries.map((entry) => {
        const draft = drafts[kind].get(entry.text) ?? entry.answered;
        const answered = isSameDraft(draft, entry.answered);
        return {
            text: entry.text,
            rows: entry.rows,
            match: entry.match,
            draft,
            targetKey: targetKeyOf(draft.target),
            fileOptions: fileOptions[kind].get(entry.text) ?? [],
            suggestion: typoLabelOf(entry),
            joinedParts: joinedPartsOf(
                entry,
                suggestions[kind].get(nameTextKey(entry.text)),
            ),
            candidates:
                entry.match === 'ambiguous'
                    ? candidateOptionsOf(entry.candidates)
                    : [],
            existingLabel:
                entry.match === 'existing'
                    ? (existing.find((option) => option.id === entry.resolvedId)
                          ?.label ?? entry.text)
                    : null,
            axesToAdd: showsAxes(entry, draft)
                ? entry.axesToAdd.map((axis) => axis.attributeTypeName)
                : [],
            isComplete: isNameSettled(entry, draft),
            notices: answered ? entry.problems.map(noticeFor) : [],
        };
    });
};

/**
 * Review names: the brands, categories and suppliers of the file that the
 * catalogue does not have, and what to do with each. Shared by the wizard
 * and the in-shell import page.
 *
 * - **Which names.** The store's preview lists every text of the file; a
 *   text that matched one entity exactly is not new and is left out, unless
 *   it is a category lacking axes its rows use (`isReviewed`), which the
 *   categories section lists apart (`gainingOptions`).
 * - **Drafts.** Each name starts from the store's decision, else from the
 *   step's default for that preview (`deriveDefaultDecisions`: the
 *   suggestion for a likely typo or a joined text, as Use existing with its
 *   target or as Rename with its name; Use existing with nothing picked for
 *   an ambiguous name; else Create). A joined text's row says why it was
 *   suggested its first name, after a re-check too (`joinedPartsOf`). Edits stay in the step until Check
 *   products or Back writes them to the store; a new preview keeps them
 *   (`mergeDrafts`). How each name matched and what its preview answered
 *   come from the decisions that preview was sent (`checkedDecisions`), so
 *   choices saved by Back are drafts again, never answers.
 * - **Back.** Saves the current choices, checked or not, to the store
 *   (`setDecisions`), then opens Upload. One still missing its value is
 *   kept as it is: Check products waits for it, and a check sent before
 *   then leaves it out (`sendableDecisions`). A check running when Back is
 *   pressed still stores its answer, but nothing navigates.
 * - **Axes.** A category row shows the preview's `axesToAdd` with a
 *   checkbox while they apply to its draft (`showsAxes`); unticked, the
 *   decision sends `addAxes: false`. A Use existing names another category
 *   whose missing axes only the next answer lists, so an answer that adds
 *   axes a row did not show when Check products was pressed stays here
 *   (`axesToReview`), the rows now showing them; pressing it again goes on.
 * - **Check products.** Needs every Use existing, Rename and Nest under to
 *   have its value and every ambiguous name to have a candidate picked
 *   (`isNameSettled`). It writes the decisions to the store (every later
 *   preview and the import send them), checks the file again and stores the
 *   answer. A clean answer goes on to Products; header problems, a refusal,
 *   names the decisions could not resolve, or axes not yet shown stay here
 *   with their sentences; no answer stays with a retry. One check at a time.
 */
export const useNamesStep = ({
    backPath,
    nextPath,
}: UseNamesStepOptions): UseNamesStepResult => {
    const navigate = useNavigate();
    const { state, actions } = useCatalogImport();
    const { mutateAsync: requestPreview } = useCatalogImportPreviewMutation();
    const brandsQuery = useAllBrandsQuery();
    const categoriesQuery = useCategoryPickerQuery();
    const suppliersQuery = useSupplierPickerQuery();

    const review = React.useMemo(
        () =>
            reviewOf(
                state.preview?.names ?? EMPTY_NAMES,
                state.decisions,
                state.checkedDecisions,
            ),
        [state.checkedDecisions, state.decisions, state.preview],
    );
    const [drafts, setDrafts] = React.useState<NameDrafts>(review.drafts);
    useResetOnKeyChange(state.preview, () =>
        setDrafts((current) => mergeDrafts(current, review)),
    );

    const [check, setCheck] = React.useState<NamesCheck>(NAMES_CHECK_IDLE);
    const isChecking = React.useRef(false);
    const isMounted = React.useRef(true);

    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const updateDraft = React.useCallback(
        (kind: NameKind, text: string, patch: Partial<NameDraft>) =>
            setDrafts((current) => {
                const draft = current[kind].get(text);
                if (!draft) {
                    return current;
                }
                const next = new Map(current[kind]);
                next.set(text, { ...draft, ...patch });
                return { ...current, [kind]: next };
            }),
        [],
    );

    const onActionChange = React.useCallback(
        (kind: NameKind, text: string, key: PickerKey) => {
            if (isAction(kind, key)) {
                updateDraft(kind, text, { action: key });
            }
        },
        [updateDraft],
    );

    const onTargetChange = React.useCallback(
        (kind: NameKind, text: string, key: PickerKey) =>
            updateDraft(kind, text, {
                target: key === null ? null : targetOfKey(String(key)),
            }),
        [updateDraft],
    );

    const onNameChange = React.useCallback(
        (kind: NameKind, text: string, name: string) =>
            updateDraft(kind, text, { name }),
        [updateDraft],
    );

    const onParentChange = React.useCallback(
        (kind: NameKind, text: string, key: PickerKey) =>
            updateDraft(kind, text, {
                parentId: key === null ? null : String(key),
            }),
        [updateDraft],
    );

    const onAddAxesChange = React.useCallback(
        (kind: NameKind, text: string, addAxes: boolean) =>
            updateDraft(kind, text, { addAxes }),
        [updateDraft],
    );

    const onAcceptAll = React.useCallback(
        (kind: NameKind) =>
            setDrafts((current) => ({
                ...current,
                [kind]: acceptAll(review.entries[kind], current[kind]),
            })),
        [review.entries],
    );

    const incomplete = incompleteCount(review.entries, drafts);
    const canContinue = state.preview !== null && incomplete === 0;

    const onContinue = React.useCallback(() => {
        if (isChecking.current || !canContinue) {
            return;
        }
        isChecking.current = true;
        setCheck({ status: 'checking' });
        const decisions = namesDecisionsOf(review.entries, drafts);
        // What the owner saw with the decisions: axes the answer adds beyond
        // these were never offered to be unticked.
        const shown = shownAxesOf(review.entries.categories, drafts.categories);
        actions.setDecisions(decisions);
        const revision = actions.previewStarted();

        void (async () => {
            try {
                const preview = await requestPreview(
                    buildCatalogImportRequest({
                        branchId: state.branch?.id ?? '',
                        file: state.file,
                        lines: state.lines,
                        decisions,
                        exclusions: state.exclusions,
                    }),
                );
                // The store outlives the step: the answer is kept even when
                // the step was left mid-check, but nothing navigates then.
                // An answer the store no longer waits for (another file,
                // branch or check since) is dropped and decides nothing.
                const isCurrent = actions.setPreview(preview, revision);
                if (!isMounted.current) {
                    return;
                }
                if (!isCurrent) {
                    setCheck(NAMES_CHECK_IDLE);
                    return;
                }
                const stay = namesCheckOfPreview(preview, decisions, shown);
                if (stay) {
                    setCheck(stay);
                    return;
                }
                setCheck(NAMES_CHECK_IDLE);
                navigate(nextPath);
            } catch (error) {
                const isCurrent = actions.previewFailed(error, revision);
                if (isMounted.current) {
                    setCheck(
                        isCurrent
                            ? namesCheckOfError(error as ErrorResponse)
                            : NAMES_CHECK_IDLE,
                    );
                }
            } finally {
                isChecking.current = false;
            }
        })();
    }, [
        actions,
        canContinue,
        drafts,
        navigate,
        nextPath,
        requestPreview,
        review.entries,
        state.branch,
        state.exclusions,
        state.file,
        state.lines,
    ]);

    // Back keeps the choices, checked or not: the store holds them, and the
    // step starts from them when it opens again. Without a preview there is
    // nothing reviewed to keep, and the stored decisions stay as they are.
    const onBack = React.useCallback(() => {
        if (state.preview !== null) {
            actions.setDecisions(namesDecisionsOf(review.entries, drafts));
        }
        navigate(backPath);
    }, [actions, backPath, drafts, navigate, review.entries, state.preview]);

    const catalog = React.useMemo<NamesCatalog>(() => {
        const brands = brandsQuery.data;
        const categories = categoriesQuery.data;
        const suppliers = suppliersQuery.data;
        if (
            brandsQuery.isError ||
            categoriesQuery.isError ||
            suppliersQuery.isError ||
            categories?.success === false ||
            suppliers?.success === false
        ) {
            return { status: 'error' };
        }
        if (!brands || !categories?.success || !suppliers?.success) {
            return { status: 'loading' };
        }
        return {
            status: 'ready',
            options: {
                brands: namedOptionsOf(brands),
                categories: categoryOptionsOf(categories.response.items),
                suppliers: namedOptionsOf(suppliers.response.items),
            },
        };
    }, [
        brandsQuery.data,
        brandsQuery.isError,
        categoriesQuery.data,
        categoriesQuery.isError,
        suppliersQuery.data,
        suppliersQuery.isError,
    ]);

    const { refetch: refetchBrands } = brandsQuery;
    const { refetch: refetchCategories } = categoriesQuery;
    const { refetch: refetchSuppliers } = suppliersQuery;
    const onRetryCatalog = React.useCallback(() => {
        void refetchBrands();
        void refetchCategories();
        void refetchSuppliers();
    }, [refetchBrands, refetchCategories, refetchSuppliers]);

    const fileOptions = React.useMemo(() => fileOptionsOf(review), [review]);
    const sections = React.useMemo(
        () =>
            NAME_KINDS.map((kind): NameSectionModel => {
                const rows = rowsOf(
                    kind,
                    review,
                    drafts,
                    fileOptions,
                    catalog.status === 'ready' ? catalog.options[kind] : [],
                    state.suggestions,
                );
                // Only categories gain axes; the split is theirs.
                const gaining = new Set(
                    kind === 'categories'
                        ? review.entries[kind]
                              .filter(isGainingOptions)
                              .map((entry) => entry.text)
                        : [],
                );
                return {
                    kind,
                    actions: NAME_ACTIONS[kind],
                    rows: rows.filter((row) => !gaining.has(row.text)),
                    gainingOptions: rows.filter((row) => gaining.has(row.text)),
                    joinedRows: rows
                        .filter((row) => row.joinedParts !== null)
                        .reduce((total, row) => total + row.rows, 0),
                };
            }),
        [catalog, drafts, fileOptions, review, state.suggestions],
    );

    return {
        hasPreview: state.preview !== null,
        sections,
        unsortedRows: unsortedRowCount(state.lines, state.exclusions),
        catalog,
        onRetryCatalog,
        check,
        incomplete,
        canContinue,
        onActionChange,
        onTargetChange,
        onNameChange,
        onParentChange,
        onAddAxesChange,
        onAcceptAll,
        onBack,
        onContinue,
    };
};
